// Survey Votes Service - App-Level Storage
import { getSupabaseUrl, getSupabaseAnonKey } from '../config/supabase';
import { queuedSupabaseOperation, getSupabaseAuthHeaders } from '../utils/supabaseWrapper';

/**
 * Get Supabase configuration for direct API calls
 */
const getSupabaseConfig = () => {
  const supabaseUrl = getSupabaseUrl();
  const supabaseKey = getSupabaseAnonKey();
  
  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Supabase configuration not found');
  }
  
  return { supabaseUrl, supabaseKey };
};

/**
 * Store a vote in Supabase
 */
export const storeVoteInSupabase = async (voteData) => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    const voteRecord = {
      user_email: voteData.userEmail,
      category_id: voteData.categoryId,
      movie_id: voteData.movieId,
      vote_value: voteData.voteValue,
      updated_at: new Date().toISOString()
    };

    // Try INSERT first with upsert behavior
    let response = await fetch(`${supabaseUrl}/rest/v1/survey_votes`, {
      method: 'POST',
      headers: await getSupabaseAuthHeaders({ 'Prefer': 'resolution=merge-duplicates,return=representation' }),
      body: JSON.stringify(voteRecord)
    });

    // If conflict (409), try UPDATE
    if (response.status === 409) {
      response = await fetch(`${supabaseUrl}/rest/v1/survey_votes?user_email=eq.${encodeURIComponent(voteData.userEmail)}&category_id=eq.${voteData.categoryId}&movie_id=eq.${voteData.movieId}`, {
        method: 'PATCH',
        headers: await getSupabaseAuthHeaders({ 'Prefer': 'return=representation' }),
        body: JSON.stringify({
          vote_value: voteData.voteValue,
          updated_at: new Date().toISOString()
        })
      });
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Vote saved to Supabase:', voteData.userEmail, voteData.movieId, voteData.voteValue);
    
    // Also save to localStorage as backup
    try {
      const votes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
      const voteKey = `${voteData.userEmail}_${voteData.categoryId}_${voteData.movieId}`;
      votes[voteKey] = {
        userEmail: voteData.userEmail,
        categoryId: voteData.categoryId,
        movieId: voteData.movieId,
        voteValue: voteData.voteValue,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('survey_votes', JSON.stringify(votes));
    } catch (localError) {
      console.warn('Could not update localStorage backup:', localError);
    }
    
    return { data: result, error: null };
  } catch (error) {
    console.error('❌ Error storing vote in Supabase:', error);
    
    // Fallback to localStorage
    try {
      const votes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
      const voteKey = `${voteData.userEmail}_${voteData.categoryId}_${voteData.movieId}`;
      
      const voteRecord = {
        userEmail: voteData.userEmail,
        categoryId: voteData.categoryId,
        movieId: voteData.movieId,
        voteValue: voteData.voteValue,
        timestamp: new Date().toISOString()
      };
      
      votes[voteKey] = voteRecord;
      localStorage.setItem('survey_votes', JSON.stringify(votes));
      return { data: voteRecord, error: null };
    } catch (localError) {
      console.error('❌ Error storing vote locally:', localError);
      return { data: null, error: localError };
    }
  }
};

/**
 * Load user votes from Supabase
 */
export const loadUserVotes = async (userInfo) => {
  try {
    const userEmail = userInfo.email || userInfo.userEmail;
    
    const votes = await queuedSupabaseOperation.select(
      'survey_votes',
      '*',
      `user_email=eq.${encodeURIComponent(userEmail)}`,
      null, // No localStorage fallback for select
      `Load user votes for ${userEmail}`
    );
    
    console.log('✅ User votes loaded from Supabase:', votes.length, 'votes');
    
    // Convert to the format expected by the app
    const userVotes = {};
    votes.forEach(vote => {
      const voteKey = `${vote.user_email}_${vote.category_id}_${vote.movie_id}`;
      userVotes[voteKey] = {
        userEmail: vote.user_email,
        categoryId: vote.category_id,
        movieId: vote.movie_id,
        voteValue: vote.vote_value,
        timestamp: vote.updated_at
      };
    });
    
    // Also save to localStorage as backup
    const allVotes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
    Object.assign(allVotes, userVotes);
    localStorage.setItem('survey_votes', JSON.stringify(allVotes));
    
    return userVotes;
  } catch (error) {
    console.error('❌ Error loading user votes from Supabase:', error);
    
    // Fallback to localStorage
    try {
      const votes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
      const userEmail = userInfo.email || userInfo.userEmail;
      
      const userVotes = Object.values(votes).filter(vote => {
        const voteUserEmail = vote.userEmail || vote.email;
        return voteUserEmail && voteUserEmail.toLowerCase() === userEmail.toLowerCase();
      });
      
      const userVotesMap = {};
      userVotes.forEach(vote => {
        const voteKey = `${vote.userEmail}_${vote.categoryId}_${vote.movieId}`;
        userVotesMap[voteKey] = vote;
      });
      
      console.log('🔄 Fallback to localStorage:', Object.keys(userVotesMap).length, 'user votes');
      return userVotesMap;
    } catch (localError) {
      console.error('❌ Error loading votes from localStorage:', localError);
      return {};
    }
  }
};

/**
 * Load all votes for a category from Supabase
 */
export const loadCategoryVotes = async (categoryId) => {
  try {
    console.log('📊 Loading votes for category:', categoryId);
    
    const votes = await queuedSupabaseOperation.select(
      'survey_votes',
      '*',
      `category_id=eq.${categoryId}`,
      null, // No localStorage fallback for select
      `Load votes for category ${categoryId}`
    );
    
    console.log('✅ Category votes loaded from Supabase:', votes.length, 'votes');
    
    return votes.map(vote => ({
      userEmail: vote.user_email,
      categoryId: vote.category_id,
      movieId: vote.movie_id,
      voteValue: vote.vote_value,
      timestamp: vote.updated_at
    }));
  } catch (error) {
    console.error('❌ Error loading category votes from Supabase:', error);
    
    // Fallback to localStorage
    try {
      const votes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
      const categoryVotes = Object.values(votes).filter(vote => 
        vote.categoryId === categoryId
      );
      
      console.log('🔄 Fallback to localStorage:', categoryVotes.length, 'category votes');
      return categoryVotes;
    } catch (localError) {
      console.error('❌ Error loading category votes from localStorage:', localError);
      return [];
    }
  }
};

